import { type FormEvent, useEffect, useRef, useState } from 'react'
import { AccountCenter } from './AccountCenter'
import { CommerceShop } from './CommerceShop'
import { ProductDetails } from './ProductDetails'
import { MotionShowcase } from './MotionShowcase'
import { categoryLabel, formatMoney, imageUrl, requestApi, serviceCover, type NailService } from './catalog'
import { useLiveRefresh } from './live-refresh'
import { readWebSession } from './web-session'

interface Page<T> { items: T[]; nextCursor: string | null }
interface Store {
  id: string; name: string; address: string; latitude: number; longitude: number
  phone: string; businessHours: string; coverUrl: string | null; customerServiceWechat: string | null
}
interface Technician {
  id: string; storeId: string; displayName: string; avatarUrl: string | null
  specialties: string[]; rating: number
}
interface Showcase {
  id: string; title: string; imageUrls: string[]; tags: string[]
  serviceId: string | null; publishedAt: string
}
interface BookingResult { id: string; bookingNo: string; status: string }
type LegalMode = 'privacy' | 'terms'

const galleryImages = ['/images/french.png', '/images/cat-eye.png', '/images/hand-care.png']
const productFromHash = () => {
  try { return window.location.hash.startsWith('#product/') ? decodeURIComponent(window.location.hash.slice(9)) : '' } catch { return '' }
}

const localDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function App() {
  const [store, setStore] = useState<Store | null>(null)
  const [services, setServices] = useState<NailService[]>([])
  const [technicians, setTechnicians] = useState<Technician[]>([])
  const [showcases, setShowcases] = useState<Showcase[]>([])
  const [loadingError, setLoadingError] = useState('')
  const [serviceId, setServiceId] = useState('')
  const [technicianId, setTechnicianId] = useState('')
  const [date, setDate] = useState(() => localDate(new Date(Date.now() + 24 * 60 * 60_000)))
  const [time, setTime] = useState('10:00')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [bookingError, setBookingError] = useState('')
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null)
  const [legalMode, setLegalMode] = useState<LegalMode | null>(null)
  const [accountOpen, setAccountOpen] = useState(false)
  const [categoryId, setCategoryId] = useState('')
  const [productId, setProductId] = useState(productFromHash)
  const [refreshToken, setRefreshToken] = useState(0)
  const openedProduct = useRef(false)

  /**
   * 实时刷新：每 20 秒对比数据版本。
   * 未登录时看公开内容版本（商品/作品/门店），登录后看自己的资产版本（卡包/订单/积分/券）。
   * 正在填写表单时只提示「有新数据」，点击后刷新，避免打断预约填写。
   */
  const liveRefresh = useLiveRefresh({
    fetchRevision: async () => {
      const session = readWebSession()
      if (session?.accessToken) {
        const mine = await requestApi<{ revision: string }>('/updates/me', {
          headers: { Authorization: `Bearer ${session.accessToken}` }
        })
        return mine.revision
      }
      const data = await requestApi<{ revision: string }>('/updates/public')
      return data.revision
    },
    apply: () => setRefreshToken((value) => value + 1)
  })

  useEffect(() => {
    const syncProduct = () => setProductId(productFromHash())
    window.addEventListener('hashchange', syncProduct)
    window.addEventListener('popstate', syncProduct)
    return () => { window.removeEventListener('hashchange', syncProduct); window.removeEventListener('popstate', syncProduct) }
  }, [])

  const openProduct = (id: string) => {
    openedProduct.current = true
    window.location.hash = `product/${encodeURIComponent(id)}`
  }
  const closeProduct = () => {
    if (openedProduct.current) { openedProduct.current = false; window.history.back() }
    else { window.history.replaceState(null, '', '#services'); setProductId('') }
  }
  const bookProduct = (product: NailService) => {
    setServiceId(product.id)
    setServices((items) => items.some((item) => item.id === product.id) ? items.map((item) => item.id === product.id ? product : item) : [...items, product])
    setBookingResult(null)
    setBookingError('')
    openedProduct.current = false
    window.history.replaceState(null, '', '#booking')
    setProductId('')
    window.requestAnimationFrame(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })
      document.querySelector<HTMLSelectElement>('#booking select')?.focus({ preventScroll: true })
    })
  }

  useEffect(() => {
    void Promise.all([
      requestApi<Page<Store>>('/stores?pageSize=1'),
      requestApi<Page<NailService>>('/services?pageSize=50'),
      requestApi<Page<Technician>>('/technicians?pageSize=50'),
      requestApi<Page<Showcase>>('/showcases?pageSize=12')
    ]).then(([storePage, servicePage, technicianPage, showcasePage]) => {
      const firstStore = storePage.items[0] ?? null
      setStore(firstStore)
      setServices(servicePage.items)
      setTechnicians(technicianPage.items)
      setShowcases(showcasePage.items)
      setServiceId(servicePage.items[0]?.id ?? '')
      setTechnicianId(technicianPage.items[0]?.id ?? '')
    }).catch((reason) => setLoadingError(reason instanceof Error ? reason.message : '门店数据读取失败'))
  }, [refreshToken])

  const featuredShowcases = showcases.slice(0, 3)
  const enabledServices = services.filter((service) => service.enabled)
  const categories = Array.from(new Set(enabledServices.map(categoryLabel))).map((label) => [label, label])
  const visibleServices = categoryId ? enabledServices.filter((service) => categoryLabel(service) === categoryId) : enabledServices
  const startsAt = date && time ? `${date}T${time}:00+08:00` : ''
  const storeName = store?.name ?? '汀风予'
  const storeAddress = store?.address ?? '山东省青岛市市北区敦化路街道徐州路176号910户'
  const storeMapUrl = store?.latitude && store?.longitude
    ? `https://uri.amap.com/marker?position=${store.longitude},${store.latitude}&name=${encodeURIComponent(storeName)}&src=tingfengyumx&coordinate=gaode&callnative=1`
    : `https://uri.amap.com/search?keyword=${encodeURIComponent(`${storeName} ${storeAddress}`)}&src=tingfengyumx&callnative=1`

  const submitBooking = async (event: FormEvent) => {
    event.preventDefault()
    if (!store || !startsAt || !privacyAccepted) return
    setSubmitting(true)
    setBookingError('')
    try {
      const result = await requestApi<BookingResult>('/web/bookings', {
        method: 'POST',
        body: JSON.stringify({
          storeId: store.id,
          serviceId,
          technicianId,
          startsAt,
          contactName: name,
          contactPhone: phone,
          notes,
          privacyAccepted,
          company: ''
        })
      })
      setBookingResult(result)
    } catch (reason) {
      setBookingError(reason instanceof Error ? reason.message : '预约提交失败')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="汀风予首页"><span>汀</span><div><strong>汀风予</strong><small>TINGFENGYU NAIL STUDIO</small></div></a>
        <nav aria-label="主导航"><a href="#services">商品</a><a href="#packages">套餐</a><a href="#works">作品</a><a href="#about">门店</a><button className="nav-account" type="button" onClick={() => setAccountOpen(true)}>账户中心</button><a className="nav-book" href="#booking">立即预约</a></nav>
      </header>

      <main id="top">
        <section className="hero-section">
          <img src="/images/hero.png" alt="汀风予裸粉色美甲作品氛围图" />
          <div className="hero-copy">
            <span className="eyebrow">QINGDAO · NAIL ATELIER</span>
            <h1>把风停在指尖<br />也把从容留给你</h1>
            <p>以细致护理为底，以克制设计表达日常。每一次预约，都为你的手型、肤色与生活方式单独留白。</p>
            <div className="hero-actions"><a className="button button--dark" href="#services">挑选心仪商品</a><a className="button button--light" href="#booking">预约到店</a></div>
            <div className="hero-notes"><span><b>01</b> 一客一消毒</span><span><b>02</b> 专属设计建议</span><span><b>03</b> 透明项目价格</span></div>
          </div>
        </section>

        {loadingError && <div className="api-notice" role="alert">{loadingError}。<button type="button" onClick={() => window.location.reload()}>重新加载</button></div>}

        <section className="section service-section" id="services">
          <div className="section-heading"><div><span className="eyebrow">THE BEAUTY COLLECTION</span><h2>挑选你的心动款式</h2></div><p>看看样品照片，了解服务细节。找到喜欢的款式，再为自己留一段变美的时间。</p></div>
          <MotionShowcase services={services} onOpenProduct={openProduct} />
          <div className="product-filters" aria-label="商品分类"><button type="button" aria-pressed={!categoryId} onClick={() => setCategoryId('')}>全部商品</button>{categories.map(([id, label]) => <button type="button" key={id} aria-pressed={categoryId === id} onClick={() => setCategoryId(id)}>{label}</button>)}</div>
          <p className="product-count">{visibleServices.length} 件商品 · 到店体验</p>
          <div className="service-grid">
            {visibleServices.map((service, index) => (
              <article className="service-card" key={service.id}>
                <button className={`service-image service-image--${index % 4}`} type="button" onClick={() => openProduct(service.id)} aria-label={`查看${service.name}商品详情`}>
                  {serviceCover(service) ? <img src={serviceCover(service)} alt={service.name} loading="lazy" /> : <span className="product-placeholder"><b>汀</b><small>图片待补充</small></span>}
                </button>
                <div className="service-body"><small>{categoryLabel(service)}</small><h3><button type="button" onClick={() => openProduct(service.id)}>{service.name}</button></h3><p>{service.description}</p><div><strong>{formatMoney(service.price)}</strong><span>{service.durationMinutes} 分钟</span></div><button className="service-detail-link" type="button" onClick={() => openProduct(service.id)}>查看商品详情 <span aria-hidden="true">→</span></button></div>
              </article>
            ))}
          </div>
          {!visibleServices.length && !loadingError && <p className="product-empty">商品正在整理中，敬请期待。</p>}
        </section>

        <section className="section commerce-section" id="packages">
          <div className="section-heading"><div><span className="eyebrow">MEMBER PACKAGES</span><h2>团购套餐与多次卡</h2></div><p>一次购买、分次到店核销；下单后可在微信小程序出示动态码完成服务。</p></div>
          <CommerceShop onRequireLogin={() => setAccountOpen(true)} refreshToken={refreshToken} />
        </section>

        <section className="manifesto">
          <span>BEAUTY IN THE QUIET</span><blockquote>“好看的指尖不必喧哗。<br />它只是刚好与你相衬。”</blockquote><p>汀风予 · 青岛</p>
        </section>

        <section className="section works-section" id="works">
          <div className="section-heading"><div><span className="eyebrow">RECENT MOODS</span><h2>近期灵感</h2></div><p>收藏喜欢的颜色与细节，让下一次到店多一份灵感。</p></div>
          <div className="works-grid">
            {galleryImages.map((fallbackImage, index) => {
              const work = featuredShowcases[index]
              const image = imageUrl(work?.imageUrls[0]) || fallbackImage
              return <figure key={work?.id ?? fallbackImage}><img src={image} alt={work?.title ?? '汀风予美甲作品'} /><figcaption><strong>{work?.title ?? ['细线法式', '暮色猫眼', '深层护理'][index]}</strong><span>{work?.tags.join(' · ') ?? '日常 · 克制 · 显白'}</span></figcaption></figure>
            })}
          </div>
        </section>

        <section className="section booking-section" id="booking">
          <div className="booking-intro"><span className="eyebrow">ONLINE BOOKING</span><h2>为你留出<br />一段安静时间</h2><p>提交后预约会立即进入管理后台。若门店未开启自动确认，店员确认后会按预留手机号与你联系。</p><ol><li><span>1</span>选择项目和美甲师</li><li><span>2</span>填写具体到店时间</li><li><span>3</span>留下联系方式并提交</li></ol></div>
          {bookingResult ? (
            <div className="booking-success"><span>✓</span><h3>预约已经提交</h3><p>预约号：<strong>{bookingResult.bookingNo}</strong></p><small>当前状态：{bookingResult.status === 'confirmed' ? '已确认' : '等待门店确认'}。请保存预约号，门店将通过预留手机号联系你。</small><button className="button button--dark" type="button" onClick={() => setBookingResult(null)}>再预约一次</button></div>
          ) : (
            <form className="booking-form" onSubmit={(event) => void submitBooking(event)}>
              <div className="form-row"><label>预约商品<select required value={serviceId} onChange={(event) => setServiceId(event.target.value)}>{enabledServices.map((service) => <option key={service.id} value={service.id}>{service.name} · {formatMoney(service.price)}</option>)}</select></label><label>美甲师<select required value={technicianId} onChange={(event) => setTechnicianId(event.target.value)}>{technicians.map((person) => <option key={person.id} value={person.id}>{person.displayName} · {person.specialties.slice(0, 2).join('/')}</option>)}</select></label></div>
              <div className="form-row"><label>到店日期<input required type="date" min={localDate(new Date())} value={date} onChange={(event) => setDate(event.target.value)} /></label><label>到店时间<input required type="time" step="60" value={time} onChange={(event) => setTime(event.target.value)} /></label></div>
              <div className="time-field"><small>可自行填写具体时间，提交时系统仍会检查营业时间、提前预约规则和美甲师是否已有冲突预约。</small></div>
              <div className="form-row"><label>你的称呼<input required maxLength={40} value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：王女士" /></label><label>手机号码<input required inputMode="numeric" pattern="1[0-9]{10}" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="用于门店确认预约" /></label></div>
              <label>备注（选填）<textarea maxLength={500} rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="例如：希望偏短、通勤显白；也可以到店沟通" /></label>
              <input className="honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" name="company" />
              <label className="privacy-check"><input required type="checkbox" checked={privacyAccepted} onChange={(event) => setPrivacyAccepted(event.target.checked)} /><span>我已阅读并同意 <button className="legal-link" type="button" onClick={(event) => { event.preventDefault(); setLegalMode('terms') }}>用户协议</button> 和 <button className="legal-link" type="button" onClick={(event) => { event.preventDefault(); setLegalMode('privacy') }}>隐私政策</button>，门店仅为预约与服务沟通使用以上信息。</span></label>
              {bookingError && <div className="form-error">{bookingError}</div>}
              <button className="button button--dark submit-button" disabled={submitting || !date || !time || !serviceId || !technicianId || !store} type="submit">{submitting ? '正在提交…' : '确认提交预约'}</button>
            </form>
          )}
        </section>

        <section className="section visit-section" id="about">
          <div className="visit-card">
            {store?.coverUrl ? <img className="visit-cover" src={store.coverUrl} alt={`${storeName}门店环境`} loading="lazy" /> : null}
            <span className="eyebrow">VISIT US</span>
            <h2>{storeName}</h2>
            <p>{storeAddress}</p>
            <p>营业时间 {store?.businessHours ?? '10:00–21:30'}</p>
            {store?.phone ? <a href={`tel:${store.phone}`}>{store.phone}</a> : <small>联系电话将在门店设置中补充</small>}
            {store?.customerServiceWechat ? <p className="visit-wechat">微信咨询：{store.customerServiceWechat}</p> : null}
            <div className="visit-actions">
              <a className="button button--light" href={storeMapUrl} target="_blank" rel="noopener noreferrer">导航到店</a>
              {store?.phone ? <a className="button button--light" href={`tel:${store.phone}`}>打电话预约</a> : null}
            </div>
          </div>
          <div className="visit-copy"><h3>慢一点，才能看见细节。</h3><p>我们会在服务前确认甲面状态、长度偏好与日常使用习惯。设计可以有灵感，但护理与卫生必须有标准。</p><div><span>器具规范消毒</span><span>项目价格透明</span><span>预约时段独立保留</span><span>维护周期如实建议</span></div></div>
        </section>
      </main>

      <footer><div className="brand brand--footer"><span>汀</span><div><strong>汀风予</strong><small>TINGFENGYU NAIL STUDIO</small></div></div><p>山东青岛 · 美甲设计与手部护理</p><nav><a href="#services">精选商品</a><a href="#booking">在线预约</a><button type="button" onClick={() => setLegalMode('privacy')}>隐私政策</button><button type="button" onClick={() => setLegalMode('terms')}>用户协议</button><a href="/admin/">商家管理</a></nav><small>© 2026 汀风予。ICP备案号将在备案完成后展示。</small></footer>

      {productId && <ProductDetails productId={productId} storeId={store?.id} onClose={closeProduct} onBook={bookProduct} refreshToken={refreshToken} />}

      <AccountCenter open={accountOpen} onClose={() => setAccountOpen(false)} refreshToken={refreshToken} />

      {liveRefresh.notice && (
        <button
          className={`live-refresh live-refresh--${liveRefresh.notice}`}
          type="button"
          onClick={liveRefresh.applyNow}
        >
          {liveRefresh.notice === 'updated'
            ? `已更新 ${liveRefresh.updatedAt}`
            : '有新数据 · 点击刷新'}
        </button>
      )}

      {legalMode && (
        <div className="legal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setLegalMode(null)
        }}>
          <article className="legal-dialog" role="dialog" aria-modal="true" aria-labelledby="legal-title">
            <header><div><span>{legalMode === 'privacy' ? 'PRIVACY POLICY' : 'USER TERMS'}</span><h2 id="legal-title">{legalMode === 'privacy' ? '汀风予隐私政策' : '汀风予用户协议'}</h2></div><button type="button" aria-label="关闭" onClick={() => setLegalMode(null)}>×</button></header>
            {legalMode === 'privacy' ? (
              <div className="legal-copy">
                <p>更新日期：2026 年 8 月 21 日。本政策适用于汀风予官网和微信小程序的预约服务。</p>
                <h3>我们收集什么</h3><p>预约时收集你的称呼、手机号码、所选服务、美甲师、到店时间和你主动填写的备注；登录小程序时还会处理微信提供的用户标识。</p>
                <h3>为什么使用</h3><p>这些信息只用于创建和管理预约、联系确认到店、提供服务、处理取消及保障账号和系统安全，不出售给第三方。</p>
                <h3>保存与保护</h3><p>数据存储在中国内地的业务服务器，采用访问控制、密码哈希、数据库隔离和备份措施。达到业务和法定保存期限后会删除或匿名化。</p>
                <h3>你的权利</h3><p>你可以联系门店查询、更正或删除个人信息，也可以在小程序中取消预约和退出登录。法律法规另有要求的除外。</p>
                <h3>联系我们</h3><p>地址：山东省青岛市市北区敦化路街道徐州路176号910户。联系电话将在门店设置和备案信息中公示。</p>
              </div>
            ) : (
              <div className="legal-copy">
                <p>更新日期：2026 年 8 月 21 日。使用预约服务即表示你理解并同意以下规则。</p>
                <h3>预约信息</h3><p>请提供真实可联系的信息并按约到店。项目价格、预计时长和可预约时间以提交时页面显示为准。</p>
                <h3>确认与取消</h3><p>未开启自动确认时，预约提交后需等待门店确认。取消时限以门店设置为准；迟到可能压缩服务时间或需要重新预约。</p>
                <h3>服务调整</h3><p>美甲师会结合甲面状态和安全要求给出建议。若实际项目、耗材或设计复杂度发生变化，会在服务前说明并由你确认。</p>
                <h3>账号与安全</h3><p>请妥善保管登录设备和账号信息，不要利用系统恶意占用时段、干扰服务或侵害他人权益。</p>
                <h3>其他</h3><p>未尽事项按页面公示、双方确认及适用法律法规处理；运营主体和联系方式以备案与门店公示信息为准。</p>
              </div>
            )}
            <button className="button button--dark legal-done" type="button" onClick={() => setLegalMode(null)}>我知道了</button>
          </article>
        </div>
      )}
    </div>
  )
}
